/**
 * SupersedeMissingInfoDialog — modal pour superseder une MI (Chunk 9.L.12).
 *
 * Backend : `PATCH /v1/missing-information/:id/supersede` (9.I.2) avec gating
 * Couche 2 actif (`missing_information.supersede` default `agent_allowed=false`).
 *
 * UX (parité 9.L.12 ReopenMissingInfoDialog confirm pattern) :
 *   - Body vide côté API (`{}`) Phase 1 — sémantique pure, pas de superseded_by_id
 *     (dette P3 9.I.3 reportée). Le broker juge en autonomie ; si une nouvelle
 *     question similaire doit être posée, il crée une nouvelle MI séparément.
 *   - Description claire : la MI est remplacée (clos sans réponse), pas équivalent
 *     à waive (sans objet) ni à fulfill (résolu avec valeur).
 *   - Submit bouton slate destructive-light (clos sans destructivité majeure).
 *   - Esc + backdrop click → close (sauf pendant submit).
 *   - Focus initial sur Cancel.
 *
 * Erreurs API (bilingues) :
 *   - 404 MISSING_INFORMATION_NOT_FOUND               → bandeau red
 *   - 409 MISSING_INFORMATION_INVALID_TRANSITION      → bandeau red (déjà superseded)
 *   - 403 GATING_HUMAN_REQUIRED                       → bandeau red
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useSupersedeMissingInformation } from "../../../lib/queries/missingInformation";
import { toast } from "../../../lib/toast";

type Props = {
  readonly missingInformationId: string;
  readonly fieldLabel: string | null;
  readonly onClose: () => void;
};

export function SupersedeMissingInfoDialog({
  missingInformationId,
  fieldLabel,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const supersede = useSupersedeMissingInformation();
  const { reset: resetMutation } = supersede;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !supersede.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, supersede.isPending]);

  const onConfirm = () => {
    if (supersede.isPending) return;
    supersede.mutate(
      { missingInformationId },
      {
        onSuccess: () => {
          toast.success(t("missing_information.supersede_dialog.success"));
          onClose();
        },
      },
    );
  };

  const apiErrorMsg =
    supersede.error instanceof ApiException
      ? getApiErrorMessage(supersede.error, lang) || t("errors.generic")
      : supersede.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!supersede.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("missing_information.supersede_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {fieldLabel?.trim() || t("missing_information.supersede_dialog.untitled")}
          </p>
          <p className="text-sm text-slate-600">
            {t("missing_information.supersede_dialog.description")}
          </p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={supersede.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={supersede.isPending}
            aria-busy={supersede.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-slate-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {supersede.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.25"
                />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {supersede.isPending
              ? t("missing_information.supersede_dialog.submitting")
              : t("missing_information.supersede_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
